import * as React from 'react';

import config from '../../config';
import { trackEvent } from '../analytics/tracking';
import { MAX_CAROUSEL_PRODUCTS } from '../home/ProductCarousel';
import { ProductPreviewContext } from '../products/ProductPreviewProvider';
import { NotificationsContext } from '../notifications/NotificationsProvider';

// Alpha-sort Brand names
const sortItemsByProperty = (a, b, property) => {
  if (a[property] < b[property]) {
    return -1; // a comes before b
  }
  if (a[property] > b[property]) {
    return 1; // b comes before a
  }
  return 0; // a and b are equal
}

// Don't show a carousel with only 1 item.
export const MIN_REORDER_ITEM_DISPLAY_COUNT = 2;

export const ReOrderItemsContext = React.createContext();

/**
 * Provides currently stocked products from a user's order history
 */
const ReOrderItemsProvider = ({children}) => {

  // For efficiency, focus on recent orders
  const MAX_ORDER_HISTORY = 12;
  const { productsBySku, sessionProductBrands } = React.useContext(ProductPreviewContext);
  const { priorOrders } = React.useContext(NotificationsContext);

  // Previously ordered and available items
  const [reOrderItems, setReOrderItems] = React.useState();
  // Previously ordered and available brands
  const [reOrderBrands, setReOrderBrands] = React.useState();

  React.useEffect(() => {
    if (productsBySku && priorOrders?.length) {
      const customerBrands = new Set();
      // Build a set of prior order Skus
      const orderItemSkus = priorOrders.slice(0, MAX_ORDER_HISTORY).reduce((acc,order) => {
        // Be more liberal with display in dev
        if (config.IS_DEV || order.status === 'completed') {
          order.items.forEach(item => {
            acc.add(item.dutchie_sku);
            customerBrands.add(item.display_info.brand);
          });
        }
        return acc;
      }, new Set());
      // For testing
      if (config.ENABLE_TEST_CUSTOMER_DATA) {
        customerBrands.add('Old Dirty Boston');
        customerBrands.add('ROVE');
        customerBrands.add('Original Massachusetts Growers');
      }
      // Select the in-stock items
      const inStockItems = [];
      orderItemSkus.forEach(sku => {
        const product = productsBySku.get(sku);
        if (product) {
          inStockItems.push(product);
        }
      });
      const inStockBrands = new Map();
      Array.from(customerBrands)
        .forEach(brand => {
        const vendor_id = sessionProductBrands.get(brand);
        if (vendor_id) {
          inStockBrands.set(brand, vendor_id);
        }
      })
      if (inStockBrands.size > 0) {
        setReOrderBrands(Array.from(inStockBrands,
          ([display_name, vendor_id]) => ({ display_name, vendor_id, itemKey:display_name }))
            .sort((a,b) => sortItemsByProperty(a,b,'display_name')));
        trackEvent(`brand_favorites_displayed`);
      }
      if (inStockItems.length >= MIN_REORDER_ITEM_DISPLAY_COUNT) {
        setReOrderItems(inStockItems.slice(0, MAX_CAROUSEL_PRODUCTS));
        trackEvent('reorder_min_items_found');
      } else {
        trackEvent('reorder_min_items_not_found');
      }
    }
  }, [productsBySku, sessionProductBrands, priorOrders]);

  return (
    <ReOrderItemsContext.Provider value={{
      reOrderItems,
      reOrderBrands,
    }}>
      {children}
    </ReOrderItemsContext.Provider>
  );
};

export default ReOrderItemsProvider;
